import produce from 'immer';
import { Reducer } from 'redux';

import { ITaskActionsType, ITaskState, ITaskTypes } from './types';

export const INITIAL_STATE: ITaskState = {
  loading: false,
  loadingTask: false,
  loadingAssignee: false,
  loadingExecution: false,
  loadingCancelingReasons: false,
  loadingTaskRanking: false,
  loadingTaskDataChart: false,
  loadingTasksByProcessInstance: false,

  sortOrder: 'asc',

  taskInstances: undefined,

  tasksAssignedToMe: undefined,
  tasksAssignedToOthers: undefined,
  completedTasks: undefined,
  deletedTasks: undefined,
  tasksHistoryAll: undefined,
  processTaskList: [],

  task: undefined,
  historyTask: undefined,

  countTasks: {
    total: 0,
    onTime: 0,
    overdue: 0,
  },

  totalTasksHistory: 0,
  totalTasksHistoryAll: 0,
  totalCompletedTasks: 0,
  totalDeletedTasks: 0,

  isAvailable: true,
  taskExecuted: false,
  taskAssigned: false,
  taskUnassigned: false,
  taskAssignedError: false,
  taskAssignedFailures: [],
  tasksDataChart: [],
  tasksStatusCount: [],
  taskRanking: [],
  cancelingReasons: [],

  isExecutingTask: false,

  filterParams: undefined,
  searchFilter: undefined,

  errorStatus: undefined,
};

const Tasks: Reducer<ITaskState, ITaskActionsType> = (
  state = INITIAL_STATE,
  action: ITaskActionsType,
) => {
  return produce(state, (draft) => {
    switch (action.type) {
      case ITaskTypes.GET_TASKS_REQUEST: {
        draft.loading = true;
        draft.task = undefined;
        draft.taskInstances = undefined;
        draft.completedTasks = undefined;
        draft.deletedTasks = undefined;
        draft.tasksHistoryAll = undefined;
        if (
          (action.payload.params.assigned && draft.isAvailable) ||
          (action.payload.params.unassigned && !draft.isAvailable)
        ) {
          draft.countTasks = { total: 0, onTime: 0, overdue: 0 };
          draft.isAvailable = !!action.payload.params.unassigned;
        }
        break;
      }
      case ITaskTypes.GET_TASKS_SUCCESS: {
        draft.loading = false;
        draft.sortOrder = action.payload.sortOrder;
        draft.taskInstances = action.payload.tasks;
        draft.countTasks = action.payload.count;
        break;
      }
      case ITaskTypes.GET_TASKS_FAILURE: {
        draft.loading = false;
        draft.countTasks = { total: 0, onTime: 0, overdue: 0 };
        break;
      }

      case ITaskTypes.GET_COMPLETED_TASKS_REQUEST: {
        draft.loading = true;
        draft.task = undefined;
        draft.tasksAssignedToMe = undefined;
        draft.tasksAssignedToOthers = undefined;
        draft.completedTasks = undefined;
        draft.deletedTasks = undefined;
        draft.tasksHistoryAll = undefined;
        break;
      }
      case ITaskTypes.GET_COMPLETED_TASKS_SUCCESS: {
        draft.loading = false;
        draft.sortOrder = action.payload.sortOrder;
        draft.completedTasks = action.payload.completedTasks;
        draft.totalCompletedTasks = Number(action.payload.totalCompletedTasks);
        break;
      }
      case ITaskTypes.GET_COMPLETED_TASKS_FAILURE: {
        draft.loading = false;
        break;
      }

      case ITaskTypes.GET_DELETED_TASKS_REQUEST: {
        draft.loading = true;
        draft.task = undefined;
        draft.tasksAssignedToMe = undefined;
        draft.tasksAssignedToOthers = undefined;
        draft.completedTasks = undefined;
        draft.deletedTasks = undefined;
        draft.tasksHistoryAll = undefined;
        break;
      }
      case ITaskTypes.GET_DELETED_TASKS_SUCCESS: {
        draft.loading = false;
        draft.sortOrder = action.payload.sortOrder;
        draft.deletedTasks = action.payload.deletedTasks;
        draft.totalDeletedTasks = Number(action.payload.totalDeletedTasks);
        break;
      }
      case ITaskTypes.GET_DELETED_TASKS_FAILURE: {
        draft.loading = false;
        break;
      }

      case ITaskTypes.GET_TASKS_HISTORY_ALL_REQUEST: {
        draft.loading = true;
        draft.task = undefined;
        draft.tasksAssignedToMe = undefined;
        draft.tasksAssignedToOthers = undefined;
        draft.completedTasks = undefined;
        draft.deletedTasks = undefined;
        draft.tasksHistoryAll = undefined;
        break;
      }
      case ITaskTypes.GET_TASKS_HISTORY_ALL_SUCCESS: {
        draft.loading = false;
        draft.sortOrder = action.payload.sortOrder;
        draft.tasksHistoryAll = action.payload.tasksHistoryAll;
        draft.totalTasksHistoryAll = Number(
          action.payload.totalTasksHistoryAll,
        );
        break;
      }
      case ITaskTypes.GET_TASKS_HISTORY_ALL_FAILURE: {
        draft.loading = false;
        break;
      }

      case ITaskTypes.GET_TASKS_HISTORY_COUNT_REQUEST: {
        break;
      }
      case ITaskTypes.GET_TASKS_HISTORY_COUNT_SUCCESS: {
        draft.totalTasksHistory = Number(action.payload.totalTasksHistory);
        break;
      }
      case ITaskTypes.GET_TASKS_HISTORY_COUNT_FAILURE: {
        break;
      }

      case ITaskTypes.RESET_TASK_ASSIGNED_ERROR: {
        draft.taskAssignedError = false;
        draft.taskAssignedFailures = [];
        break;
      }

      case ITaskTypes.UNCLAIM_TASK_REQUEST: {
        draft.loadingAssignee = true;
        draft.loading = true;
        draft.taskUnassigned = false;
        draft.taskAssignedFailures = [];
        break;
      }
      case ITaskTypes.UNCLAIM_TASK_SUCCESS: {
        draft.loadingAssignee = false;
        draft.loading = false;
        draft.taskUnassigned = true;
        break;
      }
      case ITaskTypes.UNCLAIM_TASK_FAILURE: {
        draft.loadingAssignee = false;
        draft.taskUnassigned = false;
        draft.taskAssignedFailures = action.payload.failures;
        break;
      }

      case ITaskTypes.GET_TASK_REQUEST: {
        draft.loadingTask = true;
        break;
      }
      case ITaskTypes.GET_TASK_SUCCESS: {
        draft.loadingTask = false;
        draft.task = action.payload.task;
        if (action.payload.executeTask) {
          draft.taskInstances = [action.payload.task];
        }
        break;
      }
      case ITaskTypes.GET_TASK_FAILURE: {
        draft.loadingTask = false;
        draft.errorStatus = action.payload.errorStatus;
        break;
      }

      case ITaskTypes.CLAIM_TASK_REQUEST: {
        draft.loadingAssignee = true;
        draft.taskAssigned = false;
        draft.taskAssignedError = false;
        draft.taskAssignedFailures = [];

        break;
      }
      case ITaskTypes.CLAIM_TASK_SUCCESS: {
        draft.loadingAssignee = false;
        draft.taskAssigned = true;
        break;
      }

      case ITaskTypes.CLAIM_TASK_FAILURE: {
        draft.loadingAssignee = false;
        draft.taskAssigned = false;
        draft.taskAssignedError = true;

        draft.taskAssignedFailures = action.payload.failures;

        break;
      }

      case ITaskTypes.GET_TASK_FORM_REQUEST: {
        draft.loadingTask = true;
        break;
      }
      case ITaskTypes.GET_TASK_FORM_SUCCESS: {
        draft.loadingTask = false;

        const task = draft.taskInstances?.find(
          (task) => task.id === action.payload.id,
        );

        if (task) {
          task.form = action.payload.form;
          draft.task = task;
        }
        break;
      }
      case ITaskTypes.GET_TASK_FORM_FAILURE: {
        draft.loadingTask = false;
        const foundTaskInstance = draft.taskInstances?.find(
          (task) => task.id === action.payload.id,
        );

        if (foundTaskInstance) {
          foundTaskInstance.form = null;
          draft.task = foundTaskInstance;
        }

        break;
      }

      case ITaskTypes.GET_TASK_FORM_HTML_REQUEST: {
        draft.task = undefined;
        break;
      }
      case ITaskTypes.GET_TASK_FORM_HTML_SUCCESS: {
        const foundTaskInstance = draft.taskInstances?.find(
          (task) => task.id === action.payload.id,
        );

        if (foundTaskInstance) {
          foundTaskInstance.formHTML = action.payload.form;
          draft.task = foundTaskInstance;
        }

        break;
      }
      case ITaskTypes.GET_TASK_FORM_HTML_FAILURE: {
        const foundTaskInstance = draft.taskInstances?.find(
          (task) => task.id === action.payload.id,
        );

        if (foundTaskInstance) {
          foundTaskInstance.form = null;
          draft.task = foundTaskInstance;
        }

        break;
      }

      case ITaskTypes.GET_TASK_FORM_VARIABLES_REQUEST: {
        draft.task = undefined;
        break;
      }
      case ITaskTypes.GET_TASK_FORM_VARIABLES_SUCCESS: {
        const taskFinded = draft.tasksAssignedToMe?.find(
          (task) => task.id === action.payload.id,
        );

        if (taskFinded) {
          taskFinded.formVariables = action.payload.formVariables;
          draft.task = taskFinded;
        }
        break;
      }
      case ITaskTypes.GET_TASK_FORM_VARIABLES_FAILURE: {
        break;
      }

      case ITaskTypes.EXECUTE_TASK_REQUEST: {
        draft.loadingExecution = true;
        draft.loadingAssignee = true;

        draft.taskExecuted = false;
        break;
      }
      case ITaskTypes.EXECUTE_TASK_SUCCESS: {
        draft.loadingExecution = false;
        draft.loadingAssignee = false;
        draft.taskExecuted = true;
        draft.totalTasksHistory += 1;
        draft.totalCompletedTasks += 1;
        break;
      }
      case ITaskTypes.EXECUTE_TASK_FAILURE: {
        draft.loadingExecution = false;
        draft.taskExecuted = false;
        break;
      }
      case ITaskTypes.GET_HISTORY_TASK_REQUEST: {
        draft.loadingTask = true;
        draft.historyTask = undefined;
        break;
      }
      case ITaskTypes.GET_HISTORY_TASK_SUCCESS: {
        draft.loadingTask = false;
        draft.historyTask = action.payload.historyTask;
        break;
      }
      case ITaskTypes.GET_HISTORY_TASK_FAILURE: {
        draft.loadingTask = false;
        draft.historyTask = undefined;
        break;
      }
      case ITaskTypes.GET_TASK_DATA_CHART_REQUEST: {
        draft.loadingTaskDataChart = true;
        draft.tasksDataChart = [];
        break;
      }
      case ITaskTypes.GET_TASK_DATA_CHART_SUCCESS: {
        draft.loadingTaskDataChart = false;
        draft.tasksDataChart = action.payload.tasksDataChart;
        break;
      }
      case ITaskTypes.GET_TASK_DATA_CHART_FAILURE: {
        draft.loadingTaskDataChart = false;
        break;
      }
      case ITaskTypes.GET_TASKS_STATUS_REQUEST: {
        draft.loadingTask = true;
        draft.tasksStatusCount = [];
        break;
      }
      case ITaskTypes.GET_TASKS_STATUS_SUCCESS: {
        draft.loadingTask = false;
        draft.tasksStatusCount = action.payload.tasksStatusCount;
        break;
      }
      case ITaskTypes.GET_TASKS_STATUS_FAILURE: {
        draft.loadingTask = false;
        break;
      }

      case ITaskTypes.GET_CANCELING_REASONS_REQUEST: {
        draft.loadingCancelingReasons = true;
        draft.cancelingReasons = [];
        break;
      }
      case ITaskTypes.GET_CANCELING_REASONS_SUCCESS: {
        draft.loadingCancelingReasons = false;
        draft.cancelingReasons = action.payload.cancelingReasons;
        break;
      }
      case ITaskTypes.GET_CANCELING_REASONS_FAILURE: {
        draft.loadingCancelingReasons = false;
        break;
      }

      case ITaskTypes.GET_TASK_RANKING_REQUEST: {
        draft.loadingTaskRanking = true;
        break;
      }
      case ITaskTypes.GET_TASK_RANKING_SUCCESS: {
        draft.loadingTaskRanking = false;
        draft.taskRanking = action.payload.taskRanking;
        break;
      }
      case ITaskTypes.GET_TASK_RANKING_FAILURE: {
        draft.loadingTaskRanking = false;
        break;
      }
      case ITaskTypes.GET_TASK_LIST_BY_PROCESS_INSTANCE_REQUEST: {
        draft.loadingTasksByProcessInstance = true;
        draft.processTaskList = [];
        break;
      }
      case ITaskTypes.GET_TASK_LIST_BY_PROCESS_INSTANCE_SUCCESS: {
        draft.loadingTasksByProcessInstance = false;
        draft.processTaskList = action.payload.tasks;
        break;
      }
      case ITaskTypes.GET_TASK_LIST_BY_PROCESS_INSTANCE_FAILURE: {
        draft.loadingTasksByProcessInstance = false;
        break;
      }
      case ITaskTypes.GET_FILTER_PARAMS_REQUEST: {
        draft.filterParams = action.payload.filterParams;
        draft.searchFilter = action.payload.searchFilter;
        break;
      }
      case ITaskTypes.GET_FILTER_PARAMS_SUCCESS: {
        draft.filterParams = action.payload.filterParams;
        draft.searchFilter = action.payload.searchFilter;
        break;
      }
      case ITaskTypes.GET_FILTER_PARAMS_FAILURE: {
        break;
      }

      case ITaskTypes.IS_EXECUTING_TASK_REQUEST: {
        draft.isExecutingTask = true;
        break;
      }

      case ITaskTypes.CLEAR_FILTER_PARAMS_REQUEST: {
        draft.filterParams = undefined;
        break;
      }

      case ITaskTypes.CLEAR_SEARCH_FILTER_REQUEST: {
        draft.searchFilter = undefined;
        break;
      }

      default:
    }
  });
};

export default Tasks;
